import { useSnapshot } from "valtio";
import { organizationStore } from "../../../store/OrganizationStore";
import { useEffect, useState } from "react";
import { OrganizationService } from "../../../shared/services/organizations/OrganizationService";
import showAlert from "../../../shared/utils/generic";
import { errorMessage } from "../../../shared/utils/errorMessage";
import { Brand, BrandAdToken, BrandAdTokenValue, BrandAssetKind, BrandColorItem, BrandTypographyStyle, FontVariant } from "../../../shared/models/brand.model";
import type { BrandColorFields } from "../../../shared/services/organizations/commands/cmdGetBrandColors";
import type { BrandTypographyStyleFields } from "../../../shared/services/organizations/commands/cmdGetBrandTypographyStyles";
import {
    BRAND_PATCH_FIELDS,
    BrandPatch,
    BrandPatchField,
} from "../../../shared/services/organizations/commands/cmdPatchOrganizationBrand";
import {
    ASSET_KIND_LABELS,
    AssetSettle,
    BrandAssetEntry,
    asAssetEntry,
    assetChanges,
    newAssetFields,
    rebaseAssets,
    vacatesSlot,
} from "./brandAssetEntries";

/** A field one section of the Branding tab saves: a PATCH column, or the Brand Site. */
export type BrandSectionField = BrandPatchField | "company_site_url";

/** The PATCH body for `fields`: only those that differ from what was saved, with "" as null. */
export const brandChanges = (saved: Brand, form: Brand, fields: BrandPatchField[]): BrandPatch => {
    const changes: BrandPatch = {};
    for (const field of fields) {
        if (form[field] !== saved[field]) changes[field] = form[field] === "" ? null : form[field];
    }
    return changes;
};

/** A color on the Colors section. `key` is the saved id; an added color has a key and no id yet. */
export type BrandColorEntry = BrandColorItem & { key: string };

const COLOR_ROW_FIELDS = ["hex_code", "name", "category", "usage_guidelines", "application_guidelines"] as const;

// Each legacy color slot is a column on Brand, which the multipart POST also writes.
const LEGACY_COLOR_COLUMNS: Record<string, "primary_color_code" | "secondary_color_code" | "tertiary_color_code"> = {
    "legacy:primary": "primary_color_code",
    "legacy:secondary": "secondary_color_code",
    "legacy:tertiary": "tertiary_color_code",
};

const blankToNull = (value: string | null) => (value === "" ? null : value);

/** "#" is where an added color starts; it and "" mean no color picked. */
export const isBlankHex = (hex: string) => !hex || hex === "#";

/** The PUT body for one color: only what differs from what was saved, with "" as null. A legacy slot holds only a hex. */
export const colorChanges = (saved: BrandColorItem, form: BrandColorItem): BrandColorFields => {
    const fields = saved.source === "legacy" ? (["hex_code"] as const) : COLOR_ROW_FIELDS;
    const changes: Record<string, string | null> = {};
    for (const field of fields) {
        if (blankToNull(form[field]) !== blankToNull(saved[field])) changes[field] = blankToNull(form[field]);
    }
    return changes as BrandColorFields;
};

/** The POST body for an added color. Blank text is left out. */
const newColorFields = (entry: BrandColorItem): BrandColorFields => {
    const fields: Record<string, string> = {};
    for (const field of COLOR_ROW_FIELDS) {
        const value = entry[field];
        if (value) fields[field] = value;
    }
    return fields as BrandColorFields;
};

const asEntry = (item: BrandColorItem): BrandColorEntry => ({ ...item, key: item.id });

/**
 * A style on the Typography section. An added one has a key and no id yet. `error` is what the
 * server said when this style's save failed, shown on the style until the next save.
 */
export type BrandTypographyStyleEntry = Omit<BrandTypographyStyle, "id"> & { key: string; id: number | null; error?: string };

const asTypographyEntry = (item: BrandTypographyStyle): BrandTypographyStyleEntry => ({ ...item, key: `style-${item.id}` });

const typographyFields = ({ name, category, family_name, source, usage_guidelines }: BrandTypographyStyleEntry): BrandTypographyStyleFields =>
    ({ name: name.trim(), category, family_name, source, usage_guidelines });

const typographyChanged = (saved: BrandTypographyStyleEntry, form: BrandTypographyStyleEntry) => {
    const before = typographyFields(saved);
    const after = typographyFields(form);
    return (Object.keys(after) as (keyof BrandTypographyStyleFields)[]).some((field) => after[field] !== before[field]);
};

/** An ad token value on the Ad Token Values section. An added one has a key and no uuid yet. */
export type BrandAdTokenValueEntry = Omit<BrandAdTokenValue, "uuid"> & { key: string; uuid: string | null };

const asAdTokenValueEntry = (item: BrandAdTokenValue): BrandAdTokenValueEntry => ({ ...item, key: item.uuid });

const patchValues = (brand: Brand) =>
    Object.fromEntries(BRAND_PATCH_FIELDS.map((field) => [field, brand[field]])) as Partial<Brand>;

const useOrganizationBrand = () => {

    useSnapshot(organizationStore);
    const [brandForm, setBrandForm] = useState<Brand>(new Brand);
    // The brand as the server last had it, so a section save can send only what changed.
    const [savedBrand, setSavedBrand] = useState<Brand>(new Brand);
    // The Colors section: the list as edited, and as the server last had it.
    const [colorsForm, setColorsForm] = useState<BrandColorEntry[]>([]);
    const [savedColors, setSavedColors] = useState<BrandColorEntry[]>([]);
    // The Typography section: the styles as edited, and as the server last had them.
    const [typographyForm, setTypographyForm] = useState<BrandTypographyStyleEntry[]>([]);
    const [savedTypography, setSavedTypography] = useState<BrandTypographyStyleEntry[]>([]);
    // The asset sections (Logos, ...): every asset as edited, and as the server last had it.
    // One state, so the form can be rebased on what the server sends back.
    const [assets, setAssets] = useState<{ saved: BrandAssetEntry[]; form: BrandAssetEntry[] }>({ saved: [], form: [] });
    // The Ad Token Values section: the values as edited and as last saved, and the ad tokens
    // the server says this brand can still take a value for.
    const [adTokenValuesForm, setAdTokenValuesForm] = useState<BrandAdTokenValueEntry[]>([]);
    const [savedAdTokenValues, setSavedAdTokenValues] = useState<BrandAdTokenValueEntry[]>([]);
    const [availableAdTokens, setAvailableAdTokens] = useState<BrandAdToken[]>([]);
    const [newLogo, setNewLogo] = useState<string>("");
    const [isBrandLoading, setIsBrandLoading] = useState<boolean>(false);
    const [isFontUploading, setIsFontUploading] = useState<boolean>(false);
    const service = new OrganizationService();

    const handleFileChange = () => {
        const file = brandForm.logo;
        if (file) {
          const reader = new FileReader();
          reader.onloadend = () => {
            if(typeof reader.result === 'string')
                setNewLogo(reader.result);
          };
          reader.readAsDataURL(file);
        }
      };

    useEffect(()=>{
        handleFileChange();
      }, [brandForm.logo])

    const update = async(organizationId: string) => {
        setIsBrandLoading(true);
        try {
            await service.updateOrganizationBrand(organizationId, brandForm);
            // The POST wrote every legacy column; the PATCH columns are as they were.
            setSavedBrand((prev) => ({ ...brandForm, ...patchValues(prev) }));
            showAlert("brand updated successfully","success",3000)
        } catch(e){
            showAlert(errorMessage(e),"error",3000)
        } finally {
            setIsBrandLoading(false);
        }
    }

    const getOrganizationBrand = async (organizationId: string) => {
        setIsBrandLoading(true);
        try {
            const response = await service.getOrganizationBrand(organizationId);
            if(response) {
                setBrandForm(response);
                setSavedBrand(response);
                receiveAssets(response.assets ?? []);
            }
        } catch(e){
            showAlert(errorMessage(e),"error",3000)
        } finally {
            setIsBrandLoading(false);
        }
    }

    // One section of the page. Brand Site still rides the multipart POST, sent with the
    // saved legacy columns so it never writes another section's unsaved edits.
    const saveSection = async (organizationId: string, fields: BrandSectionField[]) => {
        const changes = brandChanges(savedBrand, brandForm, fields.filter((f): f is BrandPatchField => f !== "company_site_url"));
        const site = brandForm.company_site_url;
        const siteChanged = fields.includes("company_site_url") && site !== savedBrand.company_site_url;
        if (!siteChanged && Object.keys(changes).length === 0) return;

        setIsBrandLoading(true);
        try {
            if (siteChanged) {
                await service.updateOrganizationBrand(organizationId, { ...savedBrand, logo: null, company_site_url: site });
                setSavedBrand((prev) => ({ ...prev, company_site_url: site }));
            }
            if (Object.keys(changes).length > 0) {
                const saved = await service.patchOrganizationBrand(organizationId, brandForm.id, changes);
                // The server trims text and turns blanks into null, so show what it kept.
                const values = Object.fromEntries(Object.keys(changes).map((k) => [k, saved[k as BrandPatchField]]));
                setSavedBrand((prev) => ({ ...prev, ...values }));
                setBrandForm((prev) => ({ ...prev, ...values }));
            }
            showAlert("Brand updated", "success", 3000);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        } finally {
            setIsBrandLoading(false);
        }
    }

    const loadColors = async (organizationId: string) => {
        if (!savedBrand.id) return;
        try {
            const colors = (await service.getBrandColors(organizationId, savedBrand.id)).map(asEntry);
            setColorsForm(colors);
            setSavedColors(colors);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        }
    }

    // Keeps the legacy column on the brand in step, so the multipart POST doesn't write back an old value.
    const syncLegacyColor = (item: BrandColorItem) => {
        const column = LEGACY_COLOR_COLUMNS[item.id];
        if (!column) return;
        setBrandForm((prev) => ({ ...prev, [column]: item.hex_code }));
        setSavedBrand((prev) => ({ ...prev, [column]: item.hex_code }));
    }

    // The whole Colors section: removed colors are archived, edited ones updated, added ones
    // created, one call each. A failure stops there and leaves the rest of the edits on the form.
    const saveColors = async (organizationId: string) => {
        const brandId = savedBrand.id;
        const removed = savedColors.filter((saved) => !colorsForm.some((entry) => entry.id === saved.id));
        const edited = colorsForm.flatMap((entry) => {
            const saved = savedColors.find((s) => entry.id && s.id === entry.id);
            const changes = saved ? colorChanges(saved, entry) : {};
            return Object.keys(changes).length > 0 ? [{ id: entry.id, changes }] : [];
        });
        // A legacy slot can't hold a blank hex. Clearing one archives it, which resets the
        // column to its default: how the server stores "unset".
        const cleared = edited.filter(({ id, changes }) => LEGACY_COLOR_COLUMNS[id] && isBlankHex(changes.hex_code ?? ""));
        const updated = edited.filter((e) => !cleared.includes(e));
        const added = colorsForm.filter((entry) => !entry.id);
        if (removed.length + edited.length + added.length === 0) return;
        // An added color starts as "#". Say so here rather than send it for the server to refuse.
        if (added.some((entry) => isBlankHex(entry.hex_code))) {
            showAlert("Pick a color for each added color before saving.", "error", 3000);
            return;
        }

        setIsBrandLoading(true);
        try {
            for (const color of removed) {
                await service.archiveBrandColor(organizationId, brandId, color.id);
                setSavedColors((prev) => prev.filter((c) => c.id !== color.id));
            }
            if (cleared.length > 0) {
                for (const { id } of cleared) await service.archiveBrandColor(organizationId, brandId, id);
                // The reset value is the column default, which only the server knows.
                const colors = await service.getBrandColors(organizationId, brandId);
                for (const item of colors.filter((c) => cleared.some((e) => e.id === c.id)).map(asEntry)) {
                    setSavedColors((prev) => prev.map((c) => (c.id === item.id ? item : c)));
                    setColorsForm((prev) => prev.map((c) => (c.id === item.id ? item : c)));
                    syncLegacyColor(item);
                }
            }
            for (const { id, changes } of updated) {
                const saved = asEntry(await service.updateBrandColor(organizationId, brandId, id, changes));
                setSavedColors((prev) => prev.map((c) => (c.id === id ? saved : c)));
                setColorsForm((prev) => prev.map((c) => (c.id === id ? saved : c)));
                syncLegacyColor(saved);
            }
            for (const entry of added) {
                const saved = asEntry(await service.createBrandColor(organizationId, brandId, newColorFields(entry)));
                setSavedColors((prev) => [...prev, saved]);
                setColorsForm((prev) => prev.map((c) => (c.key === entry.key ? saved : c)));
            }
            showAlert("Colors updated", "success", 3000);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        } finally {
            setIsBrandLoading(false);
        }
    }

    const loadTypographyStyles = async (organizationId: string) => {
        if (!savedBrand.id) return;
        try {
            const styles = (await service.getBrandTypographyStyles(organizationId, savedBrand.id)).map(asTypographyEntry);
            setTypographyForm(styles);
            setSavedTypography(styles);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        }
    }

    // The whole Typography section: removed styles are archived, edited ones updated, added
    // ones created, one call each. A PUT sends every field. A failure stops there, is shown on
    // the style it was about, and leaves the rest of the edits on the form.
    const saveTypographyStyles = async (organizationId: string) => {
        const brandId = savedBrand.id;
        const removed = savedTypography.filter((saved) => !typographyForm.some((entry) => entry.id === saved.id));
        const edited = typographyForm.filter((entry) => {
            const saved = savedTypography.find((s) => entry.id !== null && s.id === entry.id);
            return !!saved && typographyChanged(saved, entry);
        });
        const added = typographyForm.filter((entry) => entry.id === null);
        if (removed.length + edited.length + added.length === 0) return;
        // The server refuses a blank name or family; say so before any call goes out.
        if ([...edited, ...added].some((entry) => !entry.name.trim() || !entry.family_name)) {
            showAlert("Give each style a name and a font family before saving.", "error", 3000);
            return;
        }

        setTypographyForm((prev) => prev.map((e) => (e.error ? { ...e, error: undefined } : e)));
        setIsBrandLoading(true);
        let current: BrandTypographyStyleEntry | undefined;
        try {
            for (const style of removed) {
                current = style;
                await service.archiveBrandTypographyStyle(organizationId, brandId, style.id as number);
                setSavedTypography((prev) => prev.filter((s) => s.id !== style.id));
            }
            for (const entry of edited) {
                current = entry;
                const saved = asTypographyEntry(await service.updateBrandTypographyStyle(
                    organizationId, brandId, entry.id as number, typographyFields(entry)));
                setSavedTypography((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
                setTypographyForm((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
            }
            for (const entry of added) {
                current = entry;
                const saved = asTypographyEntry(
                    await service.createBrandTypographyStyle(organizationId, brandId, typographyFields(entry)));
                setSavedTypography((prev) => [...prev, saved]);
                setTypographyForm((prev) => prev.map((s) => (s.key === entry.key ? saved : s)));
            }
            showAlert("Typography updated", "success", 3000);
        } catch (e) {
            const message = errorMessage(e);
            showAlert(message, "error", 3000);
            const failed = current;
            // A removed style is off the form, so its error is only the alert.
            if (failed) setTypographyForm((prev) => prev.map((s) => (s.key === failed.key ? { ...s, error: message } : s)));
        } finally {
            setIsBrandLoading(false);
        }
    }

    const loadAdTokenValues = async (organizationId: string) => {
        if (!savedBrand.id) return;
        try {
            const [values, available] = await Promise.all([
                service.getBrandAdTokenValues(organizationId, savedBrand.id),
                service.getAvailableBrandAdTokens(organizationId, savedBrand.id),
            ]);
            setAdTokenValuesForm(values.map(asAdTokenValueEntry));
            setSavedAdTokenValues(values.map(asAdTokenValueEntry));
            setAvailableAdTokens(available);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        }
    }

    // The whole section, like a Django admin inline: removed values are archived, edited ones
    // updated, added ones created, one call each. A failure stops there and leaves the rest
    // of the edits on the form.
    const saveAdTokenValues = async (organizationId: string) => {
        const brandId = savedBrand.id;
        const removed = savedAdTokenValues.filter((saved) => !adTokenValuesForm.some((entry) => entry.uuid === saved.uuid));
        const edited = adTokenValuesForm.filter((entry) =>
            savedAdTokenValues.some((saved) => entry.uuid && saved.uuid === entry.uuid && saved.value !== entry.value));
        const added = adTokenValuesForm.filter((entry) => !entry.uuid);
        if (removed.length + edited.length + added.length === 0) return;
        // The server answers a blank value with a 422, so check before any call goes out.
        if ([...edited, ...added].some((entry) => !entry.value.trim())) {
            showAlert("Enter a value for every ad token before saving.", "error", 3000);
            return;
        }

        setIsBrandLoading(true);
        try {
            for (const entry of removed) {
                await service.archiveBrandAdTokenValue(organizationId, brandId, entry.uuid as string);
                setSavedAdTokenValues((prev) => prev.filter((v) => v.uuid !== entry.uuid));
            }
            for (const entry of edited) {
                const saved = asAdTokenValueEntry(
                    await service.updateBrandAdTokenValue(organizationId, brandId, entry.uuid as string, entry.value));
                setSavedAdTokenValues((prev) => prev.map((v) => (v.uuid === saved.uuid ? saved : v)));
                setAdTokenValuesForm((prev) => prev.map((v) => (v.uuid === saved.uuid ? saved : v)));
            }
            for (const entry of added) {
                const saved = asAdTokenValueEntry(await service.createBrandAdTokenValue(
                    organizationId, brandId, entry.ad_token_definition.uuid, entry.value));
                setSavedAdTokenValues((prev) => [...prev, saved]);
                setAdTokenValuesForm((prev) => prev.map((v) => (v.key === entry.key ? saved : v)));
            }
            showAlert("Ad token values updated", "success", 3000);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        } finally {
            // Whatever got saved changed which ad tokens are left to pick.
            try {
                setAvailableAdTokens(await service.getAvailableBrandAdTokens(organizationId, brandId));
            } catch (e) {
                showAlert(errorMessage(e), "error", 3000);
            }
            setIsBrandLoading(false);
        }
    }

    // The server's assets, kept under the asset edits not yet saved; see rebaseAssets.
    const receiveAssets = (next: Brand["assets"], settle?: AssetSettle) => {
        setAssets((prev) => ({
            saved: next.map(asAssetEntry),
            form: rebaseAssets(prev.form, prev.saved, next, settle),
        }));
    }

    const setAssetsForm = (update: (prev: BrandAssetEntry[]) => BrandAssetEntry[]) => {
        setAssets((prev) => ({ ...prev, form: update(prev.form) }));
    }

    // An asset or font write answers with the brand. Only its assets, default logo and fonts
    // are taken, so edits in the other sections stay on the form.
    const receiveAssetWrite = (brand: Brand, settle?: AssetSettle) => {
        const fields = { assets: brand.assets, logo_url: brand.logo_url, fonts: brand.fonts };
        setBrandForm((prev) => ({ ...prev, ...fields }));
        setSavedBrand((prev) => ({ ...prev, ...fields }));
        receiveAssets(brand.assets, settle);
    }

    // One asset section: removed assets are archived, edited ones updated, added ones
    // uploaded, one call each. An edit that frees the Primary or Square Format slot goes
    // before one that takes it. A failure stops there, is shown on the asset it was about,
    // and leaves the rest of the edits on the form.
    const saveAssets = async (organizationId: string, kind: BrandAssetKind) => {
        const brandId = savedBrand.id;
        const { saved, form } = assets;
        const removed = saved.filter((s) => s.kind === kind && !form.some((e) => e.id === s.id));
        const edited = form
            .filter((e) => e.kind === kind)
            .flatMap((entry) => {
                const before = saved.find((s) => entry.id !== null && s.id === entry.id);
                const changes = before ? assetChanges(before, entry) : {};
                return before && Object.keys(changes).length > 0 ? [{ entry, before, changes }] : [];
            })
            .sort((a, b) => Number(vacatesSlot(b.before, b.changes)) - Number(vacatesSlot(a.before, a.changes)));
        const added = form.filter((e) => e.kind === kind && e.id === null);
        if (removed.length + edited.length + added.length === 0) return;

        setAssetsForm((prev) => prev.map((e) => (e.kind === kind && e.error ? { ...e, error: undefined } : e)));
        setIsBrandLoading(true);
        let current: BrandAssetEntry | undefined;
        try {
            for (const asset of removed) {
                current = asset;
                receiveAssetWrite(await service.archiveBrandAsset(organizationId, brandId, asset.id as number));
            }
            for (const { entry, changes } of edited) {
                current = entry;
                const id = entry.id as number;
                receiveAssetWrite(await service.updateBrandAsset(organizationId, brandId, id, changes), { id });
            }
            for (const entry of added) {
                current = entry;
                const brand = await service.createBrandAsset(organizationId, brandId, entry.file as File, newAssetFields(entry));
                receiveAssetWrite(brand, { key: entry.key });
            }
            showAlert(`${ASSET_KIND_LABELS[kind]} updated`, "success", 3000);
        } catch (e) {
            const message = errorMessage(e);
            showAlert(message, "error", 3000);
            const failed = current;
            if (failed) setAssetsForm((prev) => prev.map((x) => (x.key === failed.key ? { ...x, error: message } : x)));
        } finally {
            setIsBrandLoading(false);
        }
    }

    // After a write that answers with something other than the brand: refetch it, but take
    // only what the write changed, as receiveAssetWrite does. A full reload would drop
    // unsaved edits elsewhere on the page.
    const refreshAssets = async (organizationId: string) => {
        const brand = await service.getOrganizationBrand(organizationId);
        if (brand) receiveAssetWrite(brand);
    }

    const uploadAndLinkFont = async (organizationId: string, file: File) => {
        setIsFontUploading(true);
        try {
            const font: FontVariant = await service.uploadFont(file);
            await service.linkBrandFont(organizationId, brandForm.id, font.id);
            await refreshAssets(organizationId);
            showAlert("Font uploaded and linked successfully", "success", 3000);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        } finally {
            setIsFontUploading(false);
        }
    }

    const addAsset = async (organizationId: string, file: File, makeDefault = false) => {
        setIsBrandLoading(true);
        try {
            await service.addBrandAsset(organizationId, brandForm.id, file, undefined, makeDefault);
            await refreshAssets(organizationId);
            showAlert("Logo uploaded", "success", 3000);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        } finally {
            setIsBrandLoading(false);
        }
    }

    const replaceAsset = async (organizationId: string, assetId: number, file: File) => {
        setIsBrandLoading(true);
        try {
            await service.replaceBrandAsset(organizationId, brandForm.id, assetId, file);
            await refreshAssets(organizationId);
            const kind = brandForm.assets.find((a) => a.id === assetId)?.kind ?? "LOGO";
            showAlert(kind === "LOGO" ? "Logo replaced" : "File replaced", "success", 3000);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        } finally {
            setIsBrandLoading(false);
        }
    }

    const setAssetTag = async (organizationId: string, assetId: number, tag: string | null) => {
        setIsBrandLoading(true);
        try {
            await service.setBrandAssetTag(organizationId, brandForm.id, assetId, tag);
            await refreshAssets(organizationId);
            showAlert(tag ? "Tag updated" : "Tag cleared", "success", 3000);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        } finally {
            setIsBrandLoading(false);
        }
    }

    const setDefaultLogo = async (organizationId: string, assetId: number) => {
        setIsBrandLoading(true);
        try {
            await service.setBrandDefaultLogo(organizationId, brandForm.id, assetId);
            // Refetch rather than patch locally: logo_url comes back as a fresh
            // presigned URL, and a stale one would 403 on the next render.
            await refreshAssets(organizationId);
            setBrandForm((prev) => ({ ...prev, logo: null }));
            showAlert("Default logo updated", "success", 3000);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        } finally {
            setIsBrandLoading(false);
        }
    }

    const unlinkFont = async (organizationId: string, fontId: number) => {
        try {
            await service.unlinkBrandFont(organizationId, brandForm.id, fontId);
            setBrandForm((prev) => ({
                ...prev,
                fonts: prev.fonts.filter((f) => f.id !== fontId),
            }));
            showAlert("Font removed from brand", "success", 3000);
        } catch (e) {
            showAlert(errorMessage(e), "error", 3000);
        }
    }

    return { brandForm, setBrandForm, savedBrand, update, saveSection, colorsForm, setColorsForm, savedColors, loadColors, saveColors, typographyForm, setTypographyForm, loadTypographyStyles, saveTypographyStyles, adTokenValuesForm, setAdTokenValuesForm, availableAdTokens, loadAdTokenValues, saveAdTokenValues, assetsForm: assets.form, setAssetsForm, saveAssets, getOrganizationBrand, newLogo, isBrandLoading, uploadAndLinkFont, unlinkFont, setDefaultLogo, addAsset, replaceAsset, setAssetTag, isFontUploading }
}

export default useOrganizationBrand;
